import { useCallback, useEffect, useState } from 'react';
import { getApiErrorMessage, walletService } from '@/services';
import type { WalletSummary } from '@/types/wallet';
import { notify } from '@/utils/notify';

export function useWallet(autoLoad = true) {
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [loading, setLoading] = useState(autoLoad);
  const [generating, setGenerating] = useState(false);

  const loadWallet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await walletService.getWallet();
      setWallet(res.data ?? null);
      return res.data ?? null;
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load wallet'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const generateVirtualAccount = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await walletService.generateVirtualAccount();
      await loadWallet();
      notify.success(res.message || 'Virtual account ready');
      return res.data?.virtualAccount ?? null;
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to generate virtual account'));
      return null;
    } finally {
      setGenerating(false);
    }
  }, [loadWallet]);

  useEffect(() => {
    if (autoLoad) void loadWallet();
  }, [autoLoad, loadWallet]);

  return {
    wallet,
    loading,
    generating,
    loadWallet,
    generateVirtualAccount,
    hasVirtualAccount: Boolean(wallet?.virtualAccount),
    fundingEnabled: Boolean(wallet?.fundingEnabled),
  };
}
